import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface LightboxState {
  images: string[];
  index: number;
}

interface LightboxContextValue {
  open: (images: string[], index: number) => void;
}

const LightboxContext = createContext<LightboxContextValue | null>(null);

export function useLightbox() {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error('useLightbox must be used inside LightboxProvider');
  return ctx;
}

export function LightboxProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LightboxState | null>(null);
  const touchStartX = useRef(0);

  const open = useCallback((images: string[], index: number) => {
    if (!images.length) return;
    setState({ images, index });
  }, []);

  const close = useCallback(() => setState(null), []);

  const prev = useCallback(() => {
    setState((s) => (s ? { ...s, index: (s.index - 1 + s.images.length) % s.images.length } : s));
  }, []);

  const next = useCallback(() => {
    setState((s) => (s ? { ...s, index: (s.index + 1) % s.images.length } : s));
  }, []);

  useEffect(() => {
    if (!state) return;
    document.body.classList.add('lightbox-open');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('lightbox-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [state, close, prev, next]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 50) (diff > 0 ? next : prev)();
  };

  const active = state != null;
  const hasGallery = (state?.images.length ?? 0) > 1;
  const currentSrc = state ? state.images[state.index] : '';

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      <div
        className={`lightbox${active ? ' active' : ''}${hasGallery ? ' has-gallery' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        role="dialog"
        aria-modal="true"
        aria-hidden={!active}
      >
        <button type="button" className="lightbox-close" aria-label="Fechar" onClick={close}>
          ×
        </button>
        <button
          type="button"
          className="lightbox-prev"
          aria-label="Anterior"
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
        >
          ‹
        </button>
        <img className="lightbox-img" src={currentSrc} alt="" />
        <button
          type="button"
          className="lightbox-next"
          aria-label="Próxima"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
        >
          ›
        </button>
        {state && hasGallery && (
          <span className="lightbox-counter">
            {state.index + 1} / {state.images.length}
          </span>
        )}
      </div>
    </LightboxContext.Provider>
  );
}
